import {
  type FocusEventHandler,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { NumericFormat, type NumericFormatProps } from "react-number-format";
import { CloseIcon } from "vega-icons";

import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { FormLabel } from "@vega-core/components/Form/Layout/FormLabel";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { type CoreCurrency } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import {
  getCurrencySymbol,
  useDefaultCurrencySign,
} from "@vega-core/utils/formatMoney";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { AbbreviationNumericFormat } from "./AbbreviationNumericFormat";

export type MoneyInputLayout = "horizontal" | "vertical";

export type MoneyInputProps = NumericFormatProps & {
  allowAbbreviations?: boolean;
  className?: string;
  clearable?: boolean;
  currency?: CoreCurrency;
  decimalScale?: number;
  disabled?: boolean;
  error?: string;
  errorClassName?: string;
  isOptional?: boolean;
  hideLabelTooltip?: boolean;
  label?: string;
  labelClassName?: string;
  labelTag?: ReactNode;
  layout?: MoneyInputLayout;
  name: string;
  onBlur?: FocusEventHandler;
  onClear?: () => void;
  placeholder?: string;
  value: number | string;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
  ref?: RefObject<HTMLInputElement | null>;
};

export const MoneyInput = ({
  allowAbbreviations = false,
  className,
  clearable = false,
  currency,
  decimalScale,
  disabled,
  error,
  errorClassName,
  isOptional,
  hideLabelTooltip = false,
  label,
  labelClassName,
  labelTag,
  layout = "vertical",
  name,
  onBlur,
  onClear,
  onValueChange,
  placeholder,
  value,
  fullWidth,
  constrainInputWidth = false,
  ref,
}: MoneyInputProps) => {
  const defaultCurrencySign = useDefaultCurrencySign() ?? "";
  const currencySign = currency
    ? (getCurrencySymbol(currency) ?? currency) // Show symbol if available, otherwise currency code
    : defaultCurrencySign;

  const sharedProps: NumericFormatProps & { "data-testid"?: string } = {
    allowNegative: false,
    "aria-label": label,
    autoComplete: "off" as const,
    className: cnTwMerge(
      "block w-full px-0 py-0 text-sm bg-transparent border-0 shadow-none appearance-none shadow-transparent placeholder:text-text/disabled focus:border-transparent focus:ring-0 focus:ring-offset-0",
      { "cursor-not-allowed": disabled },
    ),
    "data-testid": `${name}-money-input`,
    disabled,
    getInputRef: ref,
    name,
    onBlur,
    onValueChange,

    placeholder:
      placeholder ??
      m.placeholders_inputMoney({
        sign: currencySign,
      }),
    prefix: m.prefix_inputMoney({
      sign: currencySign,
    }),
    thousandSeparator: ",",
    value,
    isAllowed: (values) => {
      const { value } = values;

      return value.length < 16; // To prevent rounding once beyond Number.MAX_SAFE_INTEGER
    },
  };

  const isHorizontal = layout === "horizontal";

  const showClearButton =
    clearable && !isTruthy(disabled) && String(value).length > 0;

  const handleClear = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onClear?.();
  };

  const formLabel = (
    <FormLabel
      className={isHorizontal ? "mb-0 w-auto shrink-0" : undefined}
      isOptional={isOptional}
      label={label}
      labelClassName={labelClassName}
      tag={labelTag}
      tooltipContent={
        allowAbbreviations && !disabled && !hideLabelTooltip
          ? m.moneyInput_abbreviationsTooltip()
          : undefined
      }
    />
  );

  const inputBox = (
    <div
      className={cnTwMerge(
        "relative flex items-center rounded-companyBorderRadius px-2 py-1 min-h-11 shadow-sm border border-border/primary focus-within:border-companyPrimaryColor hover:border-companyPrimaryColor bg-background/primary",
        constrainInputWidth && "max-w-[50%]",
        isHorizontal && fullWidth && "flex-1",
        error && "!border-border/negative",
        { "cursor-not-allowed bg-background/component/10": disabled },
      )}
    >
      <div className="flex items-center justify-start w-full">
        {allowAbbreviations ? (
          <AbbreviationNumericFormat {...sharedProps} />
        ) : (
          <NumericFormat {...sharedProps} decimalScale={decimalScale ?? 0} />
        )}
      </div>
      {showClearButton ? (
        <ThemedIconButton
          ariaLabel={m.common_clear()}
          dataTestId={`${name}-money-input-clear`}
          icon={<CloseIcon className="h-4 w-4" />}
          onClick={handleClear}
          size="extraSmall"
          variant="noBackground"
        />
      ) : null}
    </div>
  );

  return (
    <div
      className={cnTwMerge(
        "relative cursor-text",
        isHorizontal ? "flex items-center gap-2" : fullWidth && "w-full",
        className,
      )}
    >
      {isHorizontal ? (
        formLabel
      ) : (
        <div className={cnTwMerge(fullWidth && "w-full")}>{formLabel}</div>
      )}
      {inputBox}
      {isTruthy(error) ? (
        <ValidationErrorText
          className={errorClassName}
          dataTestId={error}
          withMargin
        >
          {error}
        </ValidationErrorText>
      ) : null}
    </div>
  );
};
